import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { createAppKit } from '@reown/appkit/react';
import { near, Chain } from '@reown/appkit/networks';
import { http, reconnect } from '@wagmi/core';
import {
  arbitrumMainnet,
  arbitrumTestnet,
  bitLayerMainnet,
  bitLayerTestnet,
  nibiruMainnet,
  nibiruTestnet,
  sei,
  seiDevnet,
  bscMainnet,
  bscTestnet,
  sepoliaConfig,
  ethereumMainnet,
  avalancheMainnet,
  avalancheTestnet,
  baseMainnet,
  baseTestnet,
  optimismMainnet,
  optimismTestnet,
  polygonMainnet,
  polygonTestnet,
} from './network-config';
// Swap page specific project ID - Get from https://cloud.reown.com
const projectId = 'fbaf8eb8d9ef2872b54a16776af38712';

const useTestnet = process.env.REACT_APP_TESTNET === 'true';
const featureToggle = process.env.REACT_APP_ENABLE_TEST_FEATURE_TOGGLE === 'true';

const testEnabledUsers = (process.env.REACT_APP_TEST_ADDRESSES ?? '')
  .split(',')
  .map((addr) => addr.toLowerCase());
const enabledTestChains = (process.env.REACT_APP_TEST_CHAINS ?? '')
  .split(',')
  .map((name) => name.toLowerCase());

function isChainEnabled(chainName: string, userAddress: string | undefined): boolean {
  if (!featureToggle || !userAddress) return true;

  if (!enabledTestChains.includes(chainName.toLowerCase())) return true;

  return (
    enabledTestChains.includes(chainName.toLowerCase()) &&
    testEnabledUsers.includes(userAddress.toLowerCase())
  );
}

// To list all supported chains for deposits and withdrawals
export const getDepositChains = (useTestnet: boolean): Partial<Record<any, any>> => {
  const depositChains: any = {};

  const depositWithDrawChains = {
    bitLayer: { mainnet: bitLayerMainnet, testnet: bitLayerTestnet },
    ethereum: {
      mainnet: ethereumMainnet,
      testnet: sepoliaConfig,
    },
    nibiru: { mainnet: nibiruMainnet, testnet: nibiruTestnet },
    arbitrum: { mainnet: arbitrumMainnet, testnet: arbitrumTestnet },
    binance: { mainnet: bscMainnet, testnet: bscTestnet },
    avalanche: { mainnet: avalancheMainnet, testnet: avalancheTestnet },
  };

  for (const [chainName, config] of Object.entries(depositWithDrawChains)) {
    const lowerName = chainName.toLowerCase();
    const userAddress = localStorage.getItem('userAddress');
    const isEnabled = isChainEnabled(lowerName, userAddress ?? undefined);
    if (!isEnabled) continue;

    if (!useTestnet) {
      depositChains[chainName] = config.mainnet;
      continue;
    }

    depositChains[chainName] = config.testnet;
  }

  return depositChains;
};

export const getAllChains = (useTestnet: boolean): Partial<Record<any, any>> => {
  const chains: any = {};

  const allChains = {
    bitLayer: { mainnet: bitLayerMainnet, testnet: bitLayerTestnet },
    ethereum: {
      mainnet: ethereumMainnet,
      testnet: sepoliaConfig,
    },
    nibiru: { mainnet: nibiruMainnet, testnet: nibiruTestnet },
    arbitrum: { mainnet: arbitrumMainnet, testnet: arbitrumTestnet },
    binance: { mainnet: bscMainnet, testnet: bscTestnet },
    avalanche: { mainnet: avalancheMainnet, testnet: avalancheTestnet },
    base: { mainnet: baseMainnet, testnet: baseTestnet },
    optimism: { mainnet: optimismMainnet, testnet: optimismTestnet },
    polygon: { mainnet: polygonMainnet, testnet: polygonTestnet },
  };

  for (const [chainName, config] of Object.entries(allChains)) {
    const lowerName = chainName.toLowerCase();
    const userAddress = localStorage.getItem('userAddress');
    const isEnabled = isChainEnabled(lowerName, userAddress ?? undefined);
    if (!isEnabled) continue;

    if (!useTestnet) {
      if (config.mainnet) {
        chains[chainName] = config.mainnet;
      }
      continue;
    }

    // connect all chains mainnet and testnet. Mainnet network for swap feature testing
    if (config.mainnet) {
      chains[chainName] = config.mainnet;
    }
    if (config.testnet) {
      chains[`${chainName}-testnet`] = config.testnet;
    }
  }

  return chains;
};
export const chains = getAllChains(useTestnet);
export const depositChains = getDepositChains(useTestnet);

export const getChainId = (chainName: string) => {
  return chains[chainName]?.id ?? 0;
};

const chainList = Object.values(chains);
export const depositChainList = Object.values(depositChains) as unknown as readonly [
  Chain,
  ...Chain[],
];

export const wagmiChains = chainList;

export const AppKitSupportedNetworks = [...wagmiChains, near];

const defaultTransports = Object.fromEntries(
  (wagmiChains as readonly Chain[]).map((c) => [c.id, http()]),
) as Record<number, ReturnType<typeof http>>;

export const wagmiAdapter = new WagmiAdapter({
  projectId: projectId,
  networks: AppKitSupportedNetworks,
  transports: {
    ...defaultTransports,
    [ethereumMainnet.id]: http('https://eth.llamarpc.com'),
    [arbitrumMainnet.id]: http('https://arbitrum-one-public.nodies.app'),
    [bscMainnet.id]: http('https://endpoints.omniatech.io/v1/bsc/mainnet/public'),
    [avalancheMainnet.id]: http('https://avalanche-c-chain-rpc.publicnode.com'),
    [baseMainnet.id]: http('https://base.llamarpc.com'),
    [optimismMainnet.id]: http('https://endpoints.omniatech.io/v1/op/mainnet/public'),
    [polygonMainnet.id]: http('https://polygon.rpc.subquery.network/public'),
  },
});

// Create SEPARATE AppKit modal for swap page
// This modal will be integrated with NEAR Wallet Selector
export const connectModal = createAppKit({
  adapters: [wagmiAdapter],
  projectId: projectId,
  networks: AppKitSupportedNetworks as any,
  defaultNetwork: ethereumMainnet, // Set Ethereum as default network
  enableWalletConnect: true,
  features: {
    analytics: false,
    swaps: true,
    onramp: false,
    email: false,
    socials: false,
  },
  coinbasePreference: 'eoaOnly',
  allWallets: 'HIDE',
});

// Reconnect to persist wallet connection on page refresh
if (typeof window !== 'undefined') {
  reconnect(wagmiAdapter.wagmiConfig);
}
